<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { createEventDispatcher } from 'svelte';

  const dispatch = createEventDispatcher();

  // Props
  export let isOpen = false;
  export let currentEmail: string = '';

  // Internal state
  let step: 'email' | 'verify' = 'email';
  let newEmail: string = '';
  let verificationCode: string = '';
  let isLoading: boolean = false;
  let error: string = '';

  // Reset state when modal opens/closes
  $: if (isOpen) {
    step = 'email';
    newEmail = '';
    verificationCode = '';
    error = '';
    isLoading = false;
  }

  // Validate email format
  function isValidEmail(email: string): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  // Handle send verification code
  async function handleSendCode() {
    if (!isValidEmail(newEmail)) {
      error = $_('settings.email.invalid_format');
      return;
    }

    error = '';
    isLoading = true;

    dispatch('sendCode', {
      email: newEmail,
      onSuccess: () => {
        step = 'verify';
        isLoading = false;
      },
      onError: (errorMsg: string) => {
        error = errorMsg;
        isLoading = false;
      }
    });
  }

  // Handle verify code
  async function handleVerify() {
    if (!verificationCode || verificationCode.length < 4) {
      error = $_('settings.email.invalid_code');
      return;
    }

    error = '';
    isLoading = true;

    dispatch('verifyCode', {
      code: parseInt(verificationCode, 10),
      onSuccess: (email: string) => {
        isLoading = false;
        dispatch('close', { email });
      },
      onError: (errorMsg: string) => {
        error = errorMsg;
        isLoading = false;
      }
    });
  }

  // Handle close
  function handleClose() {
    dispatch('close', {});
  }

  // Handle back to email step
  function handleBack() {
    step = 'email';
    verificationCode = '';
    error = '';
  }
</script>

{#if isOpen}
  <dialog open>
    <article>
      <header>
        <h3>
          {#if step === 'email'}
            {currentEmail ? $_('settings.email.change_title') : $_('settings.email.add_title')}
          {:else}
            {$_('settings.email.verify_title')}
          {/if}
        </h3>
        <button aria-label="Close" class="close" on:click={handleClose}></button>
      </header>

      {#if step === 'email'}
        <div class="email-step">
          <p>{$_('settings.email.change_description')}</p>

          <label for="newEmail">{$_('settings.email.new_email_label')}</label>
          <input
            id="newEmail"
            type="email"
            bind:value={newEmail}
            placeholder={$_('settings.email.email_placeholder')}
            disabled={isLoading}
          />

          {#if newEmail && isValidEmail(newEmail)}
            <p class="confirmation-text">
              {$_('settings.email.confirmation_text', { values: { email: newEmail } })}
            </p>
          {/if}

          {#if error}
            <p class="error-text">{error}</p>
          {/if}
        </div>

        <footer class="flex space-between">
          <button type="button" class="secondary" on:click={handleClose}>
            {$_('common.cancel')}
          </button>
          <button
            type="button"
            on:click={handleSendCode}
            disabled={isLoading || !newEmail || !isValidEmail(newEmail)}
            aria-busy={isLoading}
          >
            {isLoading ? $_('settings.email.sending') : $_('settings.email.send_code')}
          </button>
        </footer>
      {:else}
        <div class="verify-step">
          <p>{$_('settings.email.verify_description', { values: { email: newEmail } })}</p>

          <label for="verificationCode">{$_('settings.email.code_label')}</label>
          <input
            id="verificationCode"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="4"
            bind:value={verificationCode}
            placeholder={$_('settings.email.code_placeholder')}
            disabled={isLoading}
          />

          {#if error}
            <p class="error-text">{error}</p>
          {/if}
        </div>

        <footer class="flex space-between">
          <button type="button" class="secondary" on:click={handleBack}>
            {$_('buttons.previous')}
          </button>
          <button
            type="button"
            on:click={handleVerify}
            disabled={isLoading || !verificationCode || verificationCode.length < 4}
            aria-busy={isLoading}
          >
            {isLoading ? $_('settings.email.verifying') : $_('settings.email.verify')}
          </button>
        </footer>
      {/if}
    </article>
  </dialog>
{/if}

<style>
  dialog {
    z-index: 1001;
  }

  .email-step,
  .verify-step {
    margin-bottom: 1rem;
  }

  .confirmation-text {
    color: var(--pico-muted-color);
    font-size: 0.875rem;
    margin-top: 0.5rem;
  }

  .error-text {
    color: var(--pico-del-color, #c62828);
    font-size: 0.875rem;
    margin-top: 0.5rem;
  }

  input[type="text"] {
    text-align: center;
    font-size: 1.5rem;
    letter-spacing: 0.5rem;
  }
</style>
